import _ from 'lodash'
import React from 'react'
import { queryFilters } from '../../Queries/queryFilters'
import { canViewPrimaryContact } from '../../State/Permissions/HasPermissions'

export const InvoiceReportColumns = (report) => ({
    description: {
        id: 'description',
        label: 'Invoice #',
        type: 'text',
        width: 15,
        value: (row) => {
            return row.ref
        },
        component: ({ value, stores }) => {
            const { row } = stores
            return <div>{value}</div>
        },
    },
    issuedOn: {
        id: 'issuedOn',
        label: 'Issue Date',
        type: 'date',
        width: 10,
        value: (row) => row.issueDate,
        queryFilters: ({ operator, value }) => {
            return queryFilters.date[operator]('issueDate', value)
        },
    },
    dueDate: {
        id: 'dueDate',
        label: 'Due Date',
        type: 'date',
        width: 10,
        value: (row) => row.dueDate,
        queryFilters: ({ operator, value }) => {
            return queryFilters.date[operator]('dueDate', value)
        },
    },
    startDate: {
        id: 'startDate',
        label: 'Work Start Date',
        type: 'date',
        width: 10,
        value: (row) => row.startDate,
        queryFilters: ({ operator, value }) => {
            return queryFilters.date[operator]('startDate', value)
        },
    },
    endDate: {
        id: 'endDate',
        label: 'Work End Date',
        type: 'date',
        width: 10,
        value: (row) => row.endDate,
        queryFilters: ({ operator, value }) => {
            return queryFilters.date[operator]('endDate', value)
        },
    },
    createdAt: {
        id: 'createdAt',
        label: 'Creation Date',
        type: 'date',
        width: 10,
        value: (row) => row.createdAt,
        queryFilters: ({ operator, value }) => {
            return queryFilters.date[operator]('createdAt', value)
        },
    },
    project: {
        id: 'project',
        label: 'Project',
        type: 'project',
        width: 20,
        value: (row) => row.project,
        queryFilters: ({ operator, value }) => {
            return queryFilters.singleId[operator]('projectId', value)
        },
    },
    contact: {
        id: 'contact',
        label: 'Client',
        type: 'contact',
        width: 20,
        value: (row) => row.contact,
        queryFilters: ({ operator, value }) => {
            return queryFilters.singleId[operator]('contactId', value)
        },
    },
    projectContact: {
        id: 'projectContact',
        label: 'Project Client',
        type: 'contact',
        width: 20,
        value: (row) => row.project.contact,
        queryFilters: ({ operator, value }) => {
            return queryFilters.singleId[operator]('projectContactId', value)
        },
    },
    projectOwner: {
        id: 'projectOwner',
        label: 'ProjectOwner',
        type: 'staff',
        width: 20,
        value: (row) => row.project.owner,
        queryFilters: ({ operator, value }) => {
            return queryFilters.singleId[operator]('projectOwnerId', value)
        },
    },
    projectPrimaryContact: {
        id: 'projectPrimaryContact',
        label: 'Project Primary Contact',
        type: 'contact',
        width: 20,
        value: (row) => row.project.primaryContact,
        queryFilters: ({ operator, value }) => {
            return queryFilters.singleId[operator](
                'projectPrimaryContactId',
                value
            )
        },
        permissions: (row) => canViewPrimaryContact(),
    },
    costCentre: {
        id: 'costCentre',
        label: 'Cost Centre',
        type: 'costCentre',
        width: 15,
        value: (row) => row.project.costCentre,
        queryFilters: ({ operator, value }) => {
            return queryFilters.singleId[operator]('projectCostCentreId', value)
        },
    },
    amountExTax: {
        id: 'amountExTax',
        label: 'Amount exc. Tax',
        type: 'currency',
        width: 10,
        value: (row) => row.totalAmount,
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('totalAmount', value)
        },
    },
    amountIncTax: {
        id: 'amountIncTax',
        label: 'Amount inc. Tax',
        type: 'currency',
        width: 10,
        value: (row) =>
            InvoiceReportColumns(report).amountExTax.value(row) +
            InvoiceReportColumns(report).amountTax.value(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'totalAmount + totalTax',
                value
            )
        },
    },
    amountTax: {
        id: 'amountTax',
        label: 'Tax Amount',
        type: 'currency',
        width: 10,
        value: (row) => row.totalTax,
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('totalTax', value)
        },
    },
    agreedFeeExTax: {
        id: 'agreedFeeExTax',
        label: 'Agreed Fee Amount exc. Tax',
        type: 'currency',
        width: 10,
        value: (row) => row.agreedAmount,
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('agreedAmount', value)
        },
    },
    variationExTax: {
        id: 'variationExTax',
        label: 'Variation Amount exc. Tax',
        type: 'currency',
        width: 10,
        value: (row) => row.variationAmount,
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('variationAmount', value)
        },
    },
    agreedFeeVariationExTax: {
        id: 'agreedFeeVariationExTax',
        label: 'Agreed Fee + Variation Amount exc. Tax',
        type: 'currency',
        width: 10,
        value: (row) =>
            InvoiceReportColumns(report).agreedFeeExTax.value(row) +
            InvoiceReportColumns(report).variationExTax.value(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'agreedAmount + variationAmount',
                value
            )
        },
    },
    reimbursementExTax: {
        id: 'reimbursementExTax',
        label: 'Reimbursement Amount exc. Tax',
        type: 'currency',
        width: 10,
        value: (row) => row.reimbursementAmount,
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('reimbursementAmount', value)
        },
    },
    agreedFeeTax: {
        id: 'agreedFeeTax',
        label: 'Agreed Fee Tax',
        type: 'currency',
        width: 10,
        value: (row) => row.agreedTax,
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('agreedTax', value)
        },
    },
    variationTax: {
        id: 'variationTax',
        label: 'Variation Tax',
        type: 'currency',
        width: 10,
        value: (row) => row.variationTax,
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('variationTax', value)
        },
    },
    agreedFeeVariationTax: {
        id: 'agreedFeeVariationTax',
        label: 'Agreed Fee + Variation Tax',
        type: 'currency',
        width: 10,
        value: (row) =>
            InvoiceReportColumns(report).agreedFeeTax.value(row) +
            InvoiceReportColumns(report).variationTax.value(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'agreedFeeTax + variationTax',
                value
            )
        },
    },
    reimbursementTax: {
        id: 'reimbursementTax',
        label: 'Reimbursement Tax',
        type: 'currency',
        width: 10,
        value: (row) => row.reimbursementAmount,
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('reimbursementAmount', value)
        },
    },
    agreedFeeIncTax: {
        id: 'agreedFeeIncTax',
        label: 'Agreed Fee Amount inc. Tax',
        type: 'currency',
        width: 10,
        value: (row) =>
            InvoiceReportColumns(report).agreedFeeExTax.value(row) +
            InvoiceReportColumns(report).agreedFeeTax.value(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'agreedFeeExTax + agreedFeeTax',
                value
            )
        },
    },
    variationIncTax: {
        id: 'variationIncTax',
        label: 'Variation Amount inc. Tax',
        type: 'currency',
        width: 10,
        value: (row) =>
            InvoiceReportColumns(report).variationExTax.value(row) +
            InvoiceReportColumns(report).variationTax.value(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'variationExTax + variationTax',
                value
            )
        },
    },
    agreedFeeVariationIncTax: {
        id: 'agreedFeeVariationIncTax',
        label: 'Agreed Fee + Variation Amount inc. Tax',
        type: 'currency',
        width: 10,
        value: (row) =>
            InvoiceReportColumns(report).agreedFeeVariationExTax.value(row) +
            InvoiceReportColumns(report).agreedFeeVariationTax.value(row),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator](
                'agreedFeeVariationExTax + agreedFeeVariationTax',
                value
            )
        },
    },
    reimbursementIncTax: {
        id: 'reimbursementIncTax',
        label: 'Reimbursement Amount inc. Tax',
        type: 'currency',
        width: 10,
        agreedFeeVariationIncTax: {
            id: 'agreedFeeVariationIncTax',
            label: 'Agreed Fee + Variation Amount inc. Tax',
            type: 'currency',
            width: 10,
            value: (row) =>
                InvoiceReportColumns(report).reimbursementExTax.value(row) +
                InvoiceReportColumns(report).reimbursementTax.value(row),
            queryFilters: ({ operator, value }) => {
                return queryFilters.number[operator](
                    'reimbursementExTax + reimbursementTax',
                    value
                )
            },
        },
    },
    synced: {
        id: 'synced',
        label: 'Synced',
        type: 'boolean',
        width: 10,
        value: (row) => row.synced,
        queryFilters: ({ operator, value }) => {
            return queryFilters.boolean[operator]('synced', value)
        },
    },
    projectStatus: {
        id: 'projectStatus',
        label: 'Project Status',
        type: 'status',
        width: 10,
        value: (row) => row.projectStatus || 'active',
        queryFilters: ({ operator, value }) => {
            return queryFilters.text[operator]('projectStatus', value)
        },
    },
})
